import {differenceInDays, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, getWeek} from 'date-fns'
import React from 'react'
import {Box, Typography, Collapse} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {formatDate} from '../../helpers/UtilityHelper'
import getColor from '../charts/colors'
import _ from 'lodash'
import {DualAxisChart} from '../charts/CompositChart'
import {FONT} from '../../styles/foundation/typography'
import {NAVY, BORDER, WHITE, SUBTLE} from '../../styles/foundation/colors'

const sectionSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px',
  overflow: 'hidden', mb: 2.5,
}

const headerSx = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  px: 2.5, py: 2, cursor: 'pointer',
  '&:hover': {bgcolor: '#fafbfc'},
}

export default function ShipingCostRevenue({data = [], from_date, to_date, flow = 'ALL'}) {
  const isReverse = flow === 'REVERSE';
  const [open, setOpen] = React.useState(true)
  React.useEffect(() => {
    // Recharts' ResponsiveContainer can measure 0 width on first mount inside the Collapse,
    // leaving the axes & legend visible but no bars/line until a later repaint. Nudge a
    // remeasure on the next frame so the data draws on initial load.
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    return () => cancelAnimationFrame(id)
  }, [])
  const rawStart = new Date(from_date)
  const rawEnd = new Date(to_date)
  // Guard against an inverted or invalid range (e.g. "This Month" on the 1st, where start is
  // after end) — eachDayOfInterval throws on start > end and whitescreens the dashboard.
  const validRange = !isNaN(rawStart) && !isNaN(rawEnd)
  const startDate = validRange && rawStart <= rawEnd ? rawStart : rawEnd
  const endDate = validRange && rawStart <= rawEnd ? rawEnd : rawStart

  let allData = (data.data || []).map((row) => {
    const awbDate = new Date(row.awbRegisteredDate)
    return {...row, week: 'Week ' + getWeek(awbDate), month: formatDate(awbDate, 'MMM yyyy'), awbRegisteredDate: formatDate(awbDate, 'yyyy-MM-dd')}
  })

  const dateDiff = differenceInDays(endDate, startDate)
  let allDates = [], allGroupedData = [], type = 'days'

  // ≤ 31 days → daily · ≤ 180 days → weekly · beyond → monthly (never an empty chart).
  if (!validRange) {
    // leave buckets empty rather than throw on an unusable range
  } else if (dateDiff < 32) {
    allDates = eachDayOfInterval({start: startDate, end: endDate}).map((r) => formatDate(r, 'yyyy-MM-dd'))
    allGroupedData = _.groupBy(allData, 'awbRegisteredDate')
  } else if (dateDiff < 180) {
    allDates = eachWeekOfInterval({start: startDate, end: endDate}).map((r) => 'Week ' + getWeek(r))
    allGroupedData = _.groupBy(allData, 'week')
    type = 'week'
  } else {
    allDates = eachMonthOfInterval({start: startDate, end: endDate}).map((r) => formatDate(r, 'MMM yyyy'))
    allGroupedData = _.groupBy(allData, 'month')
    type = 'month'
  }

  const chartData = allDates.map((k) => {
    const name = type === 'days' ? formatDate(k, 'dd-MMM') : k
    const revenue = _.sumBy(allGroupedData[k], 'invoiceValue')
    const shippingCharge = _.sumBy(allGroupedData[k], 'price')
    const perc = shippingCharge ? Math.round((shippingCharge / revenue) * 100) : 0
    return {name, revenue, perc}
  })

  const chartOptions = {
    xaxis: 'name',
    yaxis: {
      left: {
        label: isReverse ? 'Invoice Value (₹)' : 'Total Revenue (₹)',
        tickFormatter: (v) => `₹${(v / 100000).toFixed(1)}L`,
        data: [{dataKey: 'revenue', label: isReverse ? 'Invoice Value' : 'Revenue', fill: getColor(0), chartType: 'bar'}],
      },
      right: {
        label: 'Shipping Cost %',
        domain: [0, (max) => Math.ceil((max || 10) * 1.15)],
        tickFormatter: (v) => `${v}%`,
        data: [{dataKey: 'perc', unit: '%', label: 'Shipping %', fill: getColor(1), chartType: 'line'}],
      },
    },
  }

  return (
    <Box sx={sectionSx}>
      <Box sx={headerSx} onClick={() => setOpen((v) => !v)}>
        <Box>
          <Typography sx={{fontFamily: FONT, fontSize: '1rem', fontWeight: 700, color: NAVY}}>
            {isReverse ? 'Invoice Value vs Pickup Cost Trend' : 'Revenue vs Shipping Cost Trend'}
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: '0.78rem', color: SUBTLE, mt: 0.3}}>
            {isReverse ? 'Pickup cost as a percentage of invoice value over time' : 'Shipping cost as a percentage of invoice value over time'}
          </Typography>
        </Box>
        {open ? <ExpandLessIcon sx={{color: SUBTLE}} /> : <ExpandMoreIcon sx={{color: SUBTLE}} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{px: 2.5, pb: 2.5}}>
          <DualAxisChart data={chartData} chartOptions={chartOptions} />
        </Box>
      </Collapse>
    </Box>
  )
}
